import { useState, useEffect } from "react";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Receipt, Landmark, TrendingUp, PiggyBank, ShieldCheck,
  ArrowRight, ArrowLeft, Check, Lock, Mail, MessageSquare, CheckCircle2,
} from "lucide-react";

const GOALS = [
  { id: "Lower monthly expenses", label: "Lower monthly expenses", blurb: "Trim bills and free up cash each month", icon: Receipt },
  { id: "Find financing options", label: "Find financing options", blurb: "Explore loans and financing that fit your plans", icon: Landmark },
  { id: "Improve credit", label: "Improve credit", blurb: "Build a stronger credit profile over time", icon: TrendingUp },
  { id: "Build savings", label: "Build savings", blurb: "Grow an emergency fund or nest egg", icon: PiggyBank },
  { id: "Explore insurance options", label: "Explore insurance options", blurb: "Compare coverage for peace of mind", icon: ShieldCheck },
];

const TIMEFRAMES = [
  { id: "As soon as possible", label: "As soon as possible", sub: "I'm ready to act now" },
  { id: "In the next 1–3 months", label: "In the next 1–3 months", sub: "Planning ahead a little" },
  { id: "Just exploring", label: "Just exploring for now", sub: "Gathering information" },
];

export const EMAIL_CONSENT =
  "By checking this box, I agree to receive marketing emails from Step Zone with financial information, resources, and available options based on my selections, at the email address provided. Consent is not a condition of any purchase. I can unsubscribe at any time.";
export const SMS_CONSENT =
  "By checking this box, I agree to receive marketing SMS/text messages from Step Zone (including messages sent using automated technology) at the phone number provided. Consent is not a condition of any purchase. Msg & data rates may apply. Msg frequency varies. Reply STOP to opt out, HELP for help.";

const STEPS = [
  { n: 1, title: "Choose your goal" },
  { n: 2, title: "Personalize" },
  { n: 3, title: "Stay updated" },
];

export default function Home() {
  const [step, setStep] = useState(1);
  const [goal, setGoal] = useState("");
  const [timeframe, setTimeframe] = useState("");
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "" });
  const [emailOptin, setEmailOptin] = useState(false);
  const [smsOptin, setSmsOptin] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [consentText, setConsentText] = useState({ email: EMAIL_CONSENT, sms: SMS_CONSENT });

  useEffect(() => {
    api.get("/consent-text")
      .then((r) => setConsentText({
        email: r.data.email_consent_text || EMAIL_CONSENT,
        sms: r.data.sms_consent_text || SMS_CONSENT,
      }))
      .catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.first_name || !form.last_name) return toast.error("Please add your name.");
    if (!emailOptin && !smsOptin) return toast.error("Choose at least one way for us to reach you.");
    if (emailOptin && !form.email) return toast.error("Add your email to receive emails.");
    if (smsOptin && !form.phone) return toast.error("Add your mobile number to receive SMS.");
    setSubmitting(true);
    try {
      await api.post("/registrations", {
        first_name: form.first_name,
        last_name: form.last_name,
        goal, timeframe,
        email: form.email || null,
        phone: form.phone || null,
        email_optin: emailOptin,
        sms_optin: smsOptin,
        source_url: window.location.href,
      });
      setDone(true);
      toast.success("You're all set!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setStep(1); setGoal(""); setTimeframe("");
    setForm({ first_name: "", last_name: "", email: "", phone: "" });
    setEmailOptin(false); setSmsOptin(false); setDone(false);
  };

  const anim = {
    initial: { opacity: 0, y: 12 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -12 },
    transition: { duration: 0.25 },
  };

  return (
    <PublicLayout>
      <div className="mx-auto grid max-w-7xl gap-10 px-6 py-12 md:py-16 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16 lg:px-8">
        {/* Left brand panel */}
        <div className="lg:sticky lg:top-24 lg:self-start">
          <span className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 font-heading text-xs font-semibold uppercase tracking-widest text-primary">
            <span className="h-1.5 w-1.5 rounded-full bg-primary" /> Step Zone
          </span>
          <h1 className="mt-6 font-heading text-4xl font-bold leading-[1.05] tracking-tighter text-foreground sm:text-5xl lg:text-6xl">
            Take your next<br />financial step.
          </h1>
          <p className="mt-5 max-w-md font-body text-base leading-relaxed text-muted-foreground md:text-lg">
            Tell us what you'd like help with, and we'll send you relevant
            information, resources, and available options — only on the channels
            you choose.
          </p>

          <div className="mt-8 space-y-3">
            {STEPS.map((s) => (
              <div key={s.n} className="flex items-center gap-3">
                <span className={`flex h-8 w-8 items-center justify-center rounded-lg font-heading text-sm font-bold ${
                  step >= s.n ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"
                }`}>
                  {step > s.n ? <Check className="h-4 w-4" /> : s.n}
                </span>
                <span className={`font-body text-sm font-medium ${step >= s.n ? "text-foreground" : "text-muted-foreground"}`}>
                  {s.title}
                </span>
              </div>
            ))}
          </div>

          <div className="mt-8 flex items-center gap-2 rounded-xl border border-border bg-card p-4 font-body text-sm text-muted-foreground">
            <Lock className="h-4 w-4 shrink-0 text-primary" />
            No results hidden behind a form. You choose exactly how we contact you.
          </div>
        </div>

        {/* Right wizard */}
        <div>
          {/* Progress bar */}
          {!done && (
            <div className="mb-8">
              <div className="flex items-center justify-between font-heading text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <span>Step {step} of 3</span>
                <span>{STEPS[step - 1].title}</span>
              </div>
              <div className="mt-2 flex gap-2" data-testid="stepper">
                {STEPS.map((s) => (
                  <div key={s.n} className="h-1.5 flex-1 overflow-hidden rounded-full bg-secondary">
                    <motion.div
                      className="h-full rounded-full bg-primary"
                      initial={false}
                      animate={{ width: step >= s.n ? "100%" : "0%" }}
                      transition={{ duration: 0.4 }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <AnimatePresence mode="wait">
            {done ? (
              <motion.div key="done" {...anim} className="rounded-2xl border border-border bg-card p-8 shadow-[0_8px_30px_rgba(0,0,0,0.04)]" data-testid="wizard-success">
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  <CheckCircle2 className="h-8 w-8" />
                </span>
                <h2 className="mt-6 font-heading text-3xl font-bold tracking-tight text-foreground">You're all set.</h2>
                <p className="mt-3 font-body text-muted-foreground">
                  Thanks, {form.first_name}. We'll send you relevant info on{" "}
                  <span className="font-semibold text-foreground">
                    {[emailOptin && "email", smsOptin && "SMS"].filter(Boolean).join(" and ")}
                  </span>{" "}
                  about <span className="font-semibold text-foreground">{goal.toLowerCase()}</span>.
                </p>
                <div className="mt-6 rounded-xl bg-secondary p-4 font-body text-sm text-muted-foreground">
                  You can opt out anytime — reply STOP to any text, or use the unsubscribe link in any email.
                </div>
                <Button onClick={reset} variant="outline" className="mt-6 rounded-xl border-2 font-heading font-semibold" data-testid="wizard-reset">
                  Start over
                </Button>
              </motion.div>
            ) : (
              <motion.div key={step} {...anim}>
                {/* STEP 1 */}
                {step === 1 && (
                  <div data-testid="step-1">
                    <h2 className="font-heading text-2xl font-semibold tracking-tight text-foreground md:text-3xl">
                      What are you trying to improve?
                    </h2>
                    <p className="mt-2 font-body text-muted-foreground">Pick the one that fits best right now.</p>
                    <div className="mt-6 space-y-3">
                      {GOALS.map((g, i) => {
                        const active = goal === g.id;
                        return (
                          <motion.button
                            key={g.id}
                            type="button"
                            data-testid={`goal-${i}`}
                            onClick={() => { setGoal(g.id); }}
                            whileHover={{ scale: 1.01 }}
                            whileTap={{ scale: 0.99 }}
                            className={`flex w-full items-center gap-4 rounded-xl border-2 p-5 text-left transition-colors ${
                              active ? "border-primary bg-primary/5 ring-4 ring-primary/10" : "border-border bg-card hover:border-foreground/20"
                            }`}
                          >
                            <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${active ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground"}`}>
                              <g.icon className="h-5 w-5" />
                            </span>
                            <span className="flex-1">
                              <span className="block font-heading text-base font-semibold text-foreground">{g.label}</span>
                              <span className="block font-body text-sm text-muted-foreground">{g.blurb}</span>
                            </span>
                            <span className={`flex h-6 w-6 items-center justify-center rounded-full border-2 ${active ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>
                              {active && <Check className="h-3.5 w-3.5" />}
                            </span>
                          </motion.button>
                        );
                      })}
                    </div>
                    <Button
                      disabled={!goal}
                      onClick={() => setStep(2)}
                      data-testid="step-1-next"
                      className="mt-6 w-full rounded-xl bg-primary py-6 font-heading text-base font-semibold text-primary-foreground shadow-[0_4px_14px_0_rgba(0,85,255,0.39)] transition-transform hover:-translate-y-0.5 hover:bg-primary/90 disabled:opacity-40 disabled:shadow-none"
                    >
                      Continue <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                )}

                {/* STEP 2 */}
                {step === 2 && (
                  <div data-testid="step-2">
                    <h2 className="font-heading text-2xl font-semibold tracking-tight text-foreground md:text-3xl">
                      How soon do you want to act?
                    </h2>
                    <p className="mt-2 font-body text-muted-foreground">This helps us send the right resources at the right time.</p>
                    <div className="mt-6 space-y-3">
                      {TIMEFRAMES.map((t, i) => {
                        const active = timeframe === t.id;
                        return (
                          <motion.button
                            key={t.id}
                            type="button"
                            data-testid={`timeframe-${i}`}
                            onClick={() => setTimeframe(t.id)}
                            whileHover={{ scale: 1.01 }}
                            whileTap={{ scale: 0.99 }}
                            className={`flex w-full items-center justify-between rounded-xl border-2 p-5 text-left transition-colors ${
                              active ? "border-primary bg-primary/5 ring-4 ring-primary/10" : "border-border bg-card hover:border-foreground/20"
                            }`}
                          >
                            <span>
                              <span className="block font-heading text-base font-semibold text-foreground">{t.label}</span>
                              <span className="block font-body text-sm text-muted-foreground">{t.sub}</span>
                            </span>
                            <span className={`flex h-6 w-6 items-center justify-center rounded-full border-2 ${active ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>
                              {active && <Check className="h-3.5 w-3.5" />}
                            </span>
                          </motion.button>
                        );
                      })}
                    </div>
                    <div className="mt-6 flex gap-3">
                      <Button onClick={() => setStep(1)} variant="outline" data-testid="step-2-back" className="rounded-xl border-2 py-6 font-heading font-semibold">
                        <ArrowLeft className="mr-2 h-4 w-4" /> Back
                      </Button>
                      <Button
                        disabled={!timeframe}
                        onClick={() => setStep(3)}
                        data-testid="step-2-next"
                        className="flex-1 rounded-xl bg-primary py-6 font-heading text-base font-semibold text-primary-foreground shadow-[0_4px_14px_0_rgba(0,85,255,0.39)] transition-transform hover:-translate-y-0.5 hover:bg-primary/90 disabled:opacity-40 disabled:shadow-none"
                      >
                        Continue <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}

                {/* STEP 3 */}
                {step === 3 && (
                  <div data-testid="step-3">
                    <h2 className="font-heading text-2xl font-semibold tracking-tight text-foreground md:text-3xl">
                      How would you like to hear from us?
                    </h2>
                    <p className="mt-2 font-body text-muted-foreground">
                      Choose at least one channel. We'll only contact you where you say yes.
                    </p>

                    <div className="mt-6 grid gap-4 sm:grid-cols-2">
                      <Field label="First name">
                        <Input data-testid="input-first-name" value={form.first_name} onChange={set("first_name")} placeholder="Jane" className="h-12 rounded-xl border-2 border-input" />
                      </Field>
                      <Field label="Last name">
                        <Input data-testid="input-last-name" value={form.last_name} onChange={set("last_name")} placeholder="Doe" className="h-12 rounded-xl border-2 border-input" />
                      </Field>
                    </div>

                    {/* Email channel */}
                    <div className={`mt-4 rounded-xl border-2 p-4 transition-colors ${emailOptin ? "border-primary bg-primary/5" : "border-border"}`}>
                      <label className="flex cursor-pointer items-start gap-3">
                        <Checkbox data-testid="email-optin" checked={emailOptin} onCheckedChange={(v) => setEmailOptin(!!v)} className="mt-0.5 h-5 w-5 border-2 data-[state=checked]:bg-primary" />
                        <span className="flex-1">
                          <span className="flex items-center gap-2 font-heading text-base font-semibold text-foreground">
                            <Mail className="h-4 w-4 text-primary" /> Email me
                          </span>
                          <span className="mt-1 block font-body text-xs leading-relaxed text-muted-foreground">{consentText.email}</span>
                        </span>
                      </label>
                      {emailOptin && (
                        <Input data-testid="input-email" type="email" value={form.email} onChange={set("email")} placeholder="jane@example.com" className="mt-3 h-12 rounded-xl border-2 border-input" />
                      )}
                    </div>

                    {/* SMS channel */}
                    <div className={`mt-4 rounded-xl border-2 p-4 transition-colors ${smsOptin ? "border-primary bg-primary/5" : "border-border"}`}>
                      <label className="flex cursor-pointer items-start gap-3">
                        <Checkbox data-testid="sms-optin" checked={smsOptin} onCheckedChange={(v) => setSmsOptin(!!v)} className="mt-0.5 h-5 w-5 border-2 data-[state=checked]:bg-primary" />
                        <span className="flex-1">
                          <span className="flex items-center gap-2 font-heading text-base font-semibold text-foreground">
                            <MessageSquare className="h-4 w-4 text-primary" /> Text me
                          </span>
                          <span className="mt-1 block font-body text-xs leading-relaxed text-muted-foreground">{consentText.sms}</span>
                        </span>
                      </label>
                      {smsOptin && (
                        <Input data-testid="input-phone" type="tel" value={form.phone} onChange={set("phone")} placeholder="(555) 123-4567" className="mt-3 h-12 rounded-xl border-2 border-input" />
                      )}
                    </div>

                    <p className="mt-4 font-body text-xs leading-relaxed text-muted-foreground">
                      By continuing you agree to our{" "}
                      <Link to="/terms" className="font-semibold text-primary underline">Terms</Link>,{" "}
                      <Link to="/privacy" className="font-semibold text-primary underline">Privacy Policy</Link>, and{" "}
                      <Link to="/sms-terms" className="font-semibold text-primary underline">SMS Terms</Link>.
                    </p>

                    <div className="mt-6 flex gap-3">
                      <Button onClick={() => setStep(2)} variant="outline" data-testid="step-3-back" className="rounded-xl border-2 py-6 font-heading font-semibold">
                        <ArrowLeft className="mr-2 h-4 w-4" /> Back
                      </Button>
                      <Button
                        onClick={submit}
                        disabled={submitting || (!emailOptin && !smsOptin)}
                        data-testid="wizard-submit"
                        className="flex-1 rounded-xl bg-primary py-6 font-heading text-base font-semibold text-primary-foreground shadow-[0_4px_14px_0_rgba(0,85,255,0.39)] transition-transform hover:-translate-y-0.5 hover:bg-primary/90 disabled:opacity-40 disabled:shadow-none"
                      >
                        {submitting ? "Submitting…" : "Get my resources"}
                      </Button>
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* How it works */}
      <section className="border-t border-border bg-card">
        <div className="mx-auto max-w-7xl px-6 py-16 md:py-20 lg:px-8">
          <h2 className="max-w-xl font-heading text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
            Three steps. Full control.
          </h2>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {[
              { n: "01", t: "Choose your goal", d: "Tell us what you'd like help with — from lowering bills to building savings." },
              { n: "02", t: "Personalize", d: "A quick, non-sensitive question so we can send what's actually relevant." },
              { n: "03", t: "Stay updated", d: "Opt into email, SMS, or both. You decide exactly how we reach you." },
            ].map((s) => (
              <div key={s.n} className="rounded-2xl border border-border bg-background p-6">
                <span className="font-heading text-4xl font-bold tracking-tighter text-primary">{s.n}</span>
                <h3 className="mt-3 font-heading text-lg font-semibold text-foreground">{s.t}</h3>
                <p className="mt-2 font-body text-sm leading-relaxed text-muted-foreground">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}

const Field = ({ label, children }) => (
  <div>
    <Label className="mb-2 block font-body text-sm font-semibold text-foreground">{label}</Label>
    {children}
  </div>
);
